import type { definitions } from '@erebus/api-types';

/**
 * An audio note's metadata exactly as the backend sends it.
 *
 * The shape comes from the generated Swagger types. The two "deleted" fields are
 * widened to allow null: the backend sends null for a note that is still live,
 * but Swagger 2.0 has no way to say so, and the generator types them as plain
 * strings and numbers.
 */
export type IAudioNoteMetaResponse = Omit<
  definitions['models.AudioNoteMeta'],
  'deletedAt' | 'deletedBy'
> & {
  deletedAt: string | null;
  deletedBy: number | null;
};

/** An audio note's metadata with its timestamps parsed into Date objects. */
export type IAudioNoteMeta = Omit<IAudioNoteMetaResponse, 'createdAt' | 'deletedAt'> & {
  createdAt: Date;
  deletedAt: Date | null;
};

export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

export class AudioNoteValidation extends ValidationError {
  field: string;

  constructor(message: string, field: string) {
    super(message);
    this.name = 'ValidationError';
    this.field = field;
  }
}

export class AudioNoteMeta implements IAudioNoteMeta {
  ID: string;
  caseID: string;
  clientID: number;
  locationID: number;
  fileName: string;
  duration: number;
  createdAt: Date;
  deletedAt: Date | null;
  createdBy: number;
  deletedBy: number | null;

  constructor(data: IAudioNoteMetaResponse) {
    this.ID = data.ID;
    this.caseID = data.caseID;
    this.clientID = data.clientID;
    this.locationID = data.locationID;
    this.fileName = data.fileName;
    this.duration = data.duration;
    this.createdAt = new Date(data.createdAt);
    if (data.deletedAt !== null) {
      this.deletedAt = new Date(data.deletedAt);
    } else {
      this.deletedAt = null;
    }
    this.createdBy = data.createdBy;
    this.deletedBy = data.deletedBy;
  }

  static validate(value: unknown): value is IAudioNoteMetaResponse {
    if (typeof value !== 'object' || value === null) {
      throw new AudioNoteValidation('audio note must be an object', '');
    }

    const data = value as Record<string, unknown>;

    if (typeof data.ID !== 'string') {
      throw new AudioNoteValidation('ID must be a string', 'ID');
    } else if (typeof data.caseID !== 'string') {
      throw new AudioNoteValidation('caseID must be a string', 'caseID');
    } else if (typeof data.clientID !== 'number') {
      throw new AudioNoteValidation('clientID must be a number', 'clientID');
    } else if (typeof data.locationID !== 'number') {
      throw new AudioNoteValidation('locationID must be a number', 'locationID');
    } else if (typeof data.fileName !== 'string') {
      throw new AudioNoteValidation('fileName must be a string', 'fileName');
    } else if (typeof data.duration !== 'number') {
      throw new AudioNoteValidation('duration must be a number', 'duration');
    } else if (typeof data.createdAt !== 'string' || new Date(data.createdAt).getTime() === 0) {
      throw new AudioNoteValidation('createdAt at must be a Date', 'created');
    } else if (
      (typeof data.deletedAt !== 'string' && data.deletedAt !== null) ||
      (data.deletedAt !== null && new Date(data.deletedAt).getTime() === 0)
    ) {
      throw new AudioNoteValidation('deletedAt must be a Date or null', 'deletedAt');
    } else if (typeof data.createdBy !== 'number') {
      throw new AudioNoteValidation('createdBy must be a number', 'createdBy');
    } else if (typeof data.deletedBy !== 'number' && data.deletedBy !== null) {
      throw new AudioNoteValidation('deletedBy must be a number or null', 'deletedBy');
    } else {
      return true;
    }
  }
}

/**
 * The metadata part of a create request.
 *
 * Hand-written on purpose. It is sent as a JSON string inside a multipart form,
 * so the backend has no Swagger schema for it to generate from.
 */
export interface IAudioNoteDescription {
  ID: string;
  caseID: string;
  clientID: number;
  locationID: number;
  fileName: string;
  duration: number;
}
